import { useState, useEffect, useCallback } from 'react'
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Play,
  Square,
  MapPin,
  Video,
  Monitor,
  Copy,
  Download,
  Loader2,
  AlertCircle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import countryEventsService from '@/services/countryEventsService'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip'
import DataTable from '@/components/common/DataTable'
import { useConfirmacion } from '@/hooks/useConfirmacion'
import { cn } from '@/lib/utils'

const TYPE_CONFIG = {
  presencial: { label: 'Presencial', variant: 'success', Icon: MapPin },
  virtual: { label: 'Virtual', variant: 'default', Icon: Video },
  hibrido: { label: 'Híbrido', variant: 'accent', Icon: Monitor },
}

const STATUS_CONFIG = {
  draft: { label: 'Borrador', variant: 'secondary' },
  published: { label: 'Publicado', variant: 'success' },
  live: { label: 'EN VIVO', variant: 'danger' },
  completed: { label: 'Completado', variant: 'default' },
  cancelled: { label: 'Cancelado', variant: 'warning' },
}

const LEVEL_OPTIONS = [
  { value: 'basico', label: 'Básico' },
  { value: 'intermedio', label: 'Intermedio' },
  { value: 'avanzado', label: 'Avanzado' },
]

const INITIAL_FORM = {
  type: 'virtual',
  title: '',
  description: '',
  level: 'basico',
  date: '',
  endDate: '',
  capacity: 30,
  cost: 0,
  currency: 'USD',
  location: '',
  city: '',
  mapUrl: '',
  agenda: '',
  coverImage: '',
  isPublished: false,
}

function formatDate(dateStr) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('es', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function CountryEvents() {
  const { confirmar, dialogo } = useConfirmacion()
  const [events, setEvents] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const [formOpen, setFormOpen] = useState(false)
  const [editingEvent, setEditingEvent] = useState(null)
  const [form, setForm] = useState(INITIAL_FORM)
  const [saving, setSaving] = useState(false)

  const [presenterDialog, setPresenterDialog] = useState(null)
  const [registrationsDialog, setRegistrationsDialog] = useState(null)
  const [registrations, setRegistrations] = useState([])
  const [registrationsMeta, setRegistrationsMeta] = useState(null)
  const [loadingRegistrations, setLoadingRegistrations] = useState(false)
  const [confirmEndEvent, setConfirmEndEvent] = useState(null)

  const fetchEvents = useCallback(async () => {
    try {
      setError(null)
      const response = await countryEventsService.getEvents()
      setEvents(response.data || [])
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar eventos')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  const handleOpenCreate = () => {
    setEditingEvent(null)
    setForm(INITIAL_FORM)
    setFormOpen(true)
  }

  const handleOpenEdit = (event) => {
    setEditingEvent(event)
    setForm({
      type: event.type || 'virtual',
      title: event.title || '',
      description: event.description || '',
      level: event.level || 'basico',
      date: event.date ? new Date(event.date).toISOString().slice(0, 16) : '',
      endDate: event.endDate ? new Date(event.endDate).toISOString().slice(0, 16) : '',
      capacity: event.capacity ?? 30,
      cost: event.cost ?? 0,
      currency: event.currency || 'USD',
      location: event.location || '',
      city: event.city || '',
      mapUrl: event.mapUrl || '',
      agenda: event.agenda || '',
      coverImage: event.coverImage || '',
      isPublished: event.status === 'published' || event.isPublished || false,
    })
    setFormOpen(true)
  }

  const setFld = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error('El título es obligatorio')
    if (!form.date) return toast.error('La fecha es obligatoria')

    setSaving(true)
    try {
      const payload = { ...form }
      if (editingEvent) {
        await countryEventsService.updateEvent(editingEvent._id, payload)
        toast.success('Evento actualizado')
      } else {
        const response = await countryEventsService.createEvent(payload)
        toast.success('Evento creado')
        const created = response.data
        if (created?.webinar?.presenterUrl) setPresenterDialog(created)
      }
      setFormOpen(false)
      setEditingEvent(null)
      fetchEvents()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar evento')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (event) => {
    const ok = await confirmar({
      titulo: 'Eliminar evento',
      descripcion: `¿Eliminar «${event.title}»? No se puede deshacer.`,
      textoConfirmar: 'Eliminar',
      variante: 'destructiva',
    })
    if (!ok) return
    try {
      await countryEventsService.deleteEvent(event._id)
      toast.success('Evento eliminado')
      fetchEvents()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al eliminar')
    }
  }

  const handleGoLive = async (event) => {
    try {
      await countryEventsService.goLive(event._id)
      toast.success('Webinar iniciado')
      if (event.webinar?.presenterUrl) {
        window.open(event.webinar.presenterUrl, '_blank', 'noopener,noreferrer')
      }
      fetchEvents()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al iniciar webinar')
    }
  }

  const handleEndEvent = async () => {
    if (!confirmEndEvent) return
    try {
      await countryEventsService.endEvent(confirmEndEvent._id)
      toast.success('Evento finalizado')
      setConfirmEndEvent(null)
      fetchEvents()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al finalizar')
    }
  }

  const handleViewRegistrations = async (event) => {
    setRegistrationsDialog(event)
    setLoadingRegistrations(true)
    try {
      const response = await countryEventsService.getRegistrations(event._id)
      setRegistrations(response.data?.registrations || [])
      setRegistrationsMeta(response.data?.meta || null)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al cargar registros')
    } finally {
      setLoadingRegistrations(false)
    }
  }

  const handleExportCSV = () => {
    if (!registrations.length) return
    const headers = ['Nombre', 'Email', 'Teléfono', 'Organización', 'Fecha Registro', 'Estado']
    const rows = registrations.map((r) => [
      r.name || '',
      r.email || '',
      r.phone || '',
      r.organization || '',
      r.registeredAt ? formatDate(r.registeredAt) : '',
      r.status || '',
    ])
    const csv = [headers, ...rows].map((row) => row.map((c) => `"${c}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `registros-${registrationsDialog?.title || 'evento'}.csv`
    link.click()
    URL.revokeObjectURL(url)
    toast.success('CSV exportado')
  }

  const handleCopyPresenterUrl = (url) => {
    navigator.clipboard.writeText(url)
    toast.success('URL copiada')
  }

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-96" />
      </PageContainer>
    )
  }

  if (error) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertCircle size={16} />
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>{error}</span>
            <Button size="sm" variant="outline" onClick={fetchEvents}>
              Reintentar
            </Button>
          </AlertDescription>
        </Alert>
      </PageContainer>
    )
  }

  const columnasRegistros = [
    {
      clave: 'nombre', titulo: 'Nombre', prioridad: 'titulo',
      celda: (reg) => <span className="font-medium text-foreground">{reg.name || '—'}</span>,
    },
    { clave: 'email', titulo: 'Email', prioridad: 'meta', celda: (reg) => <span className="break-all">{reg.email || '—'}</span> },
    { clave: 'telefono', titulo: 'Teléfono', prioridad: 'meta', celda: (reg) => reg.phone || '—' },
    { clave: 'organizacion', titulo: 'Organización', prioridad: 'meta', celda: (reg) => reg.organization || '—' },
    {
      clave: 'fecha', titulo: 'Fecha', prioridad: 'meta',
      celda: (reg) => <span className="text-caption">{reg.registeredAt ? formatDate(reg.registeredAt) : '—'}</span>,
    },
    {
      clave: 'estado', titulo: 'Estado', prioridad: 'valor',
      celda: (reg) => (
        <Badge variant={reg.status === 'attended' ? 'success' : 'outline'}>
          {reg.status || 'registered'}
        </Badge>
      ),
    },
  ]

  const showLocationFields = form.type === 'presencial' || form.type === 'hibrido'
  const showVirtualInfo = form.type === 'virtual' || form.type === 'hibrido'

  return (
    <TooltipProvider delayDuration={200}>
      <PageContainer>
        <PageHeader
          title="Capacitaciones"
          actions={
            <Button onClick={handleOpenCreate}>
              <Plus size={16} /> Crear evento
            </Button>
          }
        />

        {events.length === 0 ? (
          <Alert variant="info">
            <AlertDescription>
              No hay eventos creados aún. Crea tu primer evento de capacitación.
            </AlertDescription>
          </Alert>
        ) : (
          <Card className="overflow-hidden">
            {/* Mobile cards */}
            <div className="md:hidden p-3 space-y-3">
              {events.map((event) => {
                const t = TYPE_CONFIG[event.type] || TYPE_CONFIG.virtual
                const s = STATUS_CONFIG[event.status] || STATUS_CONFIG.draft
                const TIcon = t.Icon
                const registered = event.registrationsCount ?? 0
                const capacity = event.capacity ?? 30
                const progress = capacity > 0 ? (registered / capacity) * 100 : 0
                return (
                  <div
                    key={event._id}
                    className="rounded-md border border-line p-3"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex flex-wrap gap-1.5">
                        <Badge variant={t.variant} className="gap-1">
                          <TIcon size={11} /> {t.label}
                        </Badge>
                        <Badge
                          variant={s.variant}
                          className={cn(
                            'gap-1',
                            event.status === 'live' && 'animate-pulse'
                          )}
                        >
                          {event.status === 'live' && (
                            <span className="h-1.5 w-1.5 rounded-full bg-current" />
                          )}
                          {s.label}
                        </Badge>
                      </div>
                    </div>
                    <p className="font-semibold text-foreground">{event.title}</p>
                    <p className="text-caption text-subtle mt-0.5">
                      {formatDate(event.date)}
                    </p>
                    <div className="mt-2">
                      <div className="flex justify-between text-caption mb-1">
                        <span className="text-subtle">Inscritos</span>
                        <span className="font-medium text-foreground">
                          {registered}/{capacity}
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-surface-muted dark:bg-neutral-700 overflow-hidden">
                        <div
                          className="h-full bg-primary"
                          style={{ width: `${Math.min(progress, 100)}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-3 pt-3 border-t border-line">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenEdit(event)}
                      >
                        <Edit size={14} /> Editar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewRegistrations(event)}
                      >
                        <Eye size={14} /> Registros
                      </Button>
                      {(event.type === 'virtual' || event.type === 'hibrido') &&
                        event.status === 'published' && (
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => handleGoLive(event)}
                          >
                            <Play size={14} /> Iniciar
                          </Button>
                        )}
                      {event.status === 'live' && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => setConfirmEndEvent(event)}
                        >
                          <Square size={14} /> Finalizar
                        </Button>
                      )}
                      {event.status === 'draft' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-danger"
                          onClick={() => handleDelete(event)}
                          aria-label="Eliminar"
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-body-sm">
                <thead className="bg-surface-muted border-b border-line">
                  <tr>
                    {['Título', 'Tipo', 'Fecha', 'Inscritos', 'Estado', ''].map(
                      (h, i) => (
                        <th
                          key={i}
                          className="text-left px-4 py-3 font-semibold text-foreground last:text-right"
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {events.map((event) => {
                    const t = TYPE_CONFIG[event.type] || TYPE_CONFIG.virtual
                    const s = STATUS_CONFIG[event.status] || STATUS_CONFIG.draft
                    const TIcon = t.Icon
                    const registered = event.registrationsCount ?? 0
                    const capacity = event.capacity ?? 30
                    const progress = capacity > 0 ? (registered / capacity) * 100 : 0
                    return (
                      <tr key={event._id} className="hover:bg-surface-muted dark:hover:bg-neutral-800/60">
                        <td className="px-4 py-3 font-medium text-foreground">
                          {event.title}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={t.variant} className="gap-1">
                            <TIcon size={11} /> {t.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-foreground">
                          {formatDate(event.date)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 min-w-28">
                            <span className="text-foreground tabular-nums">
                              {registered}/{capacity}
                            </span>
                            <div className="flex-1 h-1.5 rounded-full bg-surface-muted dark:bg-neutral-700 overflow-hidden">
                              <div
                                className="h-full bg-primary"
                                style={{ width: `${Math.min(progress, 100)}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant={s.variant}
                            className={cn(
                              'gap-1',
                              event.status === 'live' && 'animate-pulse'
                            )}
                          >
                            {event.status === 'live' && (
                              <span className="h-1.5 w-1.5 rounded-full bg-current" />
                            )}
                            {s.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-0.5">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={() => handleOpenEdit(event)}
                                  aria-label="Editar"
                                >
                                  <Edit size={14} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Editar</TooltipContent>
                            </Tooltip>
                            {event.status === 'draft' && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={() => handleDelete(event)}
                                    aria-label="Eliminar"
                                    className="text-danger hover:bg-danger-50 dark:hover:bg-danger-500/15"
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Eliminar</TooltipContent>
                              </Tooltip>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={() => handleViewRegistrations(event)}
                                  aria-label="Registros"
                                >
                                  <Eye size={14} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Ver registros</TooltipContent>
                            </Tooltip>
                            {(event.type === 'virtual' ||
                              event.type === 'hibrido') &&
                              event.status === 'published' && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon-sm"
                                      onClick={() => handleGoLive(event)}
                                      aria-label="Iniciar"
                                      className="text-success hover:bg-success-50 dark:hover:bg-success-500/15"
                                    >
                                      <Play size={14} />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Iniciar webinar</TooltipContent>
                                </Tooltip>
                              )}
                            {event.status === 'live' && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon-sm"
                                    onClick={() => setConfirmEndEvent(event)}
                                    aria-label="Finalizar"
                                    className="text-danger hover:bg-danger-50 dark:hover:bg-danger-500/15"
                                  >
                                    <Square size={14} />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Finalizar</TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Form dialog */}
        <Dialog open={formOpen} onOpenChange={(open) => !open && setFormOpen(false)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>
                {editingEvent ? 'Editar evento' : 'Crear evento'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Tipo de evento</Label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {[
                    { value: 'presencial', label: 'Presencial', Icon: MapPin },
                    { value: 'virtual', label: 'Virtual', Icon: Video },
                    { value: 'hibrido', label: 'Híbrido', Icon: Monitor },
                  ].map((opt) => {
                    const Icon = opt.Icon
                    return (
                      <label
                        key={opt.value}
                        className={cn(
                          'flex items-center justify-center gap-2 rounded-md border px-3 py-2.5 cursor-pointer transition-colors',
                          form.type === opt.value
                            ? 'border-primary bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                            : 'border-line hover:bg-surface-muted dark:hover:bg-neutral-800/60'
                        )}
                      >
                        <input
                          type="radio"
                          name="type"
                          value={opt.value}
                          checked={form.type === opt.value}
                          onChange={() => setFld('type', opt.value)}
                          className="sr-only"
                        />
                        <Icon size={16} />
                        <span className="text-body-sm font-medium">{opt.label}</span>
                      </label>
                    )
                  })}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ev-title">Título *</Label>
                <Input
                  id="ev-title"
                  value={form.title}
                  onChange={(e) => setFld('title', e.target.value)}
                  maxLength={150}
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ev-desc">Descripción</Label>
                <Textarea
                  id="ev-desc"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setFld('description', e.target.value)}
                  maxLength={1000}
                />
              </div>

              <div className="space-y-1.5">
                <Label>Nivel</Label>
                <Select value={form.level} onValueChange={(v) => setFld('level', v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEVEL_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="ev-date">Fecha y hora *</Label>
                  <Input
                    id="ev-date"
                    type="datetime-local"
                    className="dark:[color-scheme:dark]"
                    value={form.date}
                    onChange={(e) => setFld('date', e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ev-end">Fecha fin</Label>
                  <Input
                    id="ev-end"
                    type="datetime-local"
                    className="dark:[color-scheme:dark]"
                    value={form.endDate}
                    onChange={(e) => setFld('endDate', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ev-cap">Capacidad</Label>
                  <Input
                    id="ev-cap"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={form.capacity}
                    onChange={(e) => setFld('capacity', Number(e.target.value))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ev-cost">Costo</Label>
                  <Input
                    id="ev-cost"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={form.cost}
                    onChange={(e) => setFld('cost', Number(e.target.value))}
                  />
                  <p className="text-caption text-subtle">0 = Gratis</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Moneda</Label>
                  <Select
                    value={form.currency}
                    onValueChange={(v) => setFld('currency', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {showLocationFields && (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="ev-loc">Ubicación</Label>
                    <Input
                      id="ev-loc"
                      value={form.location}
                      onChange={(e) => setFld('location', e.target.value)}
                      placeholder="Dirección del evento"
                    />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor="ev-city">Ciudad</Label>
                      <Input
                        id="ev-city"
                        value={form.city}
                        onChange={(e) => setFld('city', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="ev-map">Google Maps</Label>
                      <Input
                        id="ev-map"
                        value={form.mapUrl}
                        onChange={(e) => setFld('mapUrl', e.target.value)}
                        placeholder="Link de Google Maps"
                      />
                    </div>
                  </div>
                </div>
              )}

              {showVirtualInfo && !editingEvent && (
                <Alert variant="info">
                  <AlertDescription>
                    Se generará automáticamente una sala de webinar al crear el evento.
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="ev-agenda">Agenda</Label>
                <Textarea
                  id="ev-agenda"
                  rows={4}
                  value={form.agenda}
                  onChange={(e) => setFld('agenda', e.target.value)}
                  maxLength={2000}
                  placeholder="Describe el programa del evento…"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="ev-cover">Imagen de portada (URL)</Label>
                <Input
                  id="ev-cover"
                  value={form.coverImage}
                  onChange={(e) => setFld('coverImage', e.target.value)}
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={form.isPublished}
                  onCheckedChange={(v) => setFld('isPublished', !!v)}
                />
                <span className="text-body-sm">Publicar evento</span>
              </label>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setFormOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Guardando…
                  </>
                ) : (
                  'Guardar'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Presenter URL dialog */}
        <Dialog
          open={!!presenterDialog}
          onOpenChange={(open) => !open && setPresenterDialog(null)}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Webinar creado</DialogTitle>
            </DialogHeader>
            <Alert variant="success">
              <AlertDescription>
                El evento ha sido creado exitosamente.
              </AlertDescription>
            </Alert>
            <div className="space-y-1.5">
              <Label>Tu URL de presentador (solo para ti)</Label>
              <Input
                readOnly
                value={presenterDialog?.webinar?.presenterUrl || ''}
                className="font-mono text-body-sm"
              />
            </div>
            <p className="text-caption text-subtle">
              Los asistentes recibirán su link al registrarse.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPresenterDialog(null)}>
                Cerrar
              </Button>
              <Button
                onClick={() =>
                  handleCopyPresenterUrl(presenterDialog?.webinar?.presenterUrl)
                }
              >
                <Copy size={16} /> Copiar URL
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Registrations dialog */}
        <Dialog
          open={!!registrationsDialog}
          onOpenChange={(open) => !open && setRegistrationsDialog(null)}
        >
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <DialogTitle className="truncate">
                    Registros - {registrationsDialog?.title}
                  </DialogTitle>
                  <Badge variant="default">
                    {registrationsMeta?.total ?? registrations.length}
                  </Badge>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleExportCSV}
                  disabled={registrations.length === 0}
                >
                  <Download size={14} /> CSV
                </Button>
              </div>
            </DialogHeader>

            {loadingRegistrations ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-10" />
                ))}
              </div>
            ) : registrations.length === 0 ? (
              <Alert variant="info">
                <AlertDescription>No hay registros para este evento.</AlertDescription>
              </Alert>
            ) : (
              <DataTable
                tarjetasEnMovil
                etiquetaTabla="Registros del evento"
                columnas={columnasRegistros}
                filas={registrations}
                claveDeFila={(reg, idx) => String(reg._id || idx)}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Confirm end event */}
        <Dialog
          open={!!confirmEndEvent}
          onOpenChange={(open) => !open && setConfirmEndEvent(null)}
        >
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Finalizar evento</DialogTitle>
            </DialogHeader>
            <p className="text-body-sm text-foreground">
              ¿Finalizar &quot;{confirmEndEvent?.title}&quot;? Esta acción no se puede
              deshacer.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmEndEvent(null)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleEndEvent}>
                Finalizar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {dialogo}
      </PageContainer>
    </TooltipProvider>
  )
}
